import { NetworkStatus, useQuery, useReactiveVar } from '@apollo/client'
import HoverTooltip from '@deltek/specification-client/core/Feedbacks/HoverTooltip'
import { CurrentUserInfoQuery } from '@deltek/specification-client/core/GraphQL/Queries/currentUserInfo.queries'
import { CurrentUserInfoResponse } from '@deltek/specification-client/core/Models/currentUserInfo'
import {
    GetFilterInput,
    GetSortInput,
    ProjectItem,
} from '@deltek/specification-client/core/Models/projectItem'
import GridContainer from '@deltek/specification-client/core/Surfaces/GridContainer'
import GridItem from '@deltek/specification-client/core/Surfaces/GridItem'
import { debounce } from '@deltek/specification-client/core/Utilities/formUtils'
import { updateKpMainLocalStore } from '@deltek/specification-client/core/Utilities/localstorageUtils'
import { isEqual } from '@deltek/specification-client/core/Utilities/objectUtils'
import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import {
    ActionColumnProps,
    SpecColumn,
    SpecFilters,
    SpecIdType,
    SpecSortingRule,
    SpecTable,
    SpecTableState,
    TableFetchNextDataProps,
} from '@deltek/specification-client/core/Widgets/SpecTable'
import { SpecTableTitleSettingsProps } from '@deltek/specification-client/core/Widgets/SpecTable/SpecTableTitleSettings'
import FilterSelectText from '@deltek/specification-client/core/Widgets/SpecTable/filters/FilterSelectText'
import useSpecTableOptions from '@deltek/specification-client/core/Widgets/SpecTable/useSpecTableOptions'
import IconButton from '@material-ui/core/IconButton'
import { Theme, makeStyles } from '@material-ui/core/styles'
import MoreVertIcon from '@material-ui/icons/MoreVert'
import StarIcon from '@material-ui/icons/Star'
import StarBorderIcon from '@material-ui/icons/StarBorder'
import { ProjectGroupCountItem, TableProjectItem } from 'Models/project'
import { userInfo } from 'api/graphQL/reactiveVars/user'
import clsx from 'clsx'
import { format, parseISO } from 'date-fns'
import React, { useCallback, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { Cell, CellProps, Row } from 'react-table'
import { getAssociationModifier } from '../..'
import { getLocalStorageValue } from '../../../projectUtils'
import Popout from '../Popout'
import PopoutInfoContent from '../PopoutInfoContent'
import { ProjectFilterContext } from '../ProjectFilterContext'
import ProjectGroupDialog from '../ProjectGroupDialog'
import RemoveProjectGroupDialog from '../RemoveProjectGroupDialog'
import ToggleIconButton from '../ToggleIconButton'
import ActionColumnPopup from './ActionColumnPopup'
import ProjectGroupActionPopup from './ProjectGroupActionPopup'

const useStyles = makeStyles((theme: Theme) => ({
    specTableStyle: {
        '& .table-title-container': {
            fontSize: pxToRem(16),
            textTransform: 'uppercase',
            marginLeft: theme.spacing(-1.5),
        },
        '& .MuiSvgIcon-root.arrow-down, .MuiSvgIcon-root.arrow-right': {
            marginLeft: theme.spacing(-1),
            color: 'rgba(0, 0, 0, 0.54)',
        },
        '& .MuiGrid-justify-content-xs-space-between': {
            backgroundColor: '#343334',
        },
        '& .MuiList-padding': {
            maxHeight: 300,
        },
        '& .table-title-settings-container': {
            backgroundColor: '#f2f2f2',
        },
        '& .light button.expand-all-button, .light button.collapse-all-button': {
            color: `${theme.palette.primary.main}`,
            border: `1px solid ${theme.palette.primary.main}`,
        },
        '& .MuiButton-containedPrimary': {
            backgroundColor: '#343334',
            color: theme.palette.common.white,
        },
        '& .view-as-table.active, .view-filter.active': {
            backgroundColor: theme.palette.primary.main,
        },
        '& .load-next-button': {
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.common.white,
        },
        '& .tr.grouped-row.expanded .td.grouped-cell': {
            backgroundColor: theme.palette.common.white,
        },
        '& .td.grouped-cell': {
            color: '#343334',
            fontWeight: 'bold',
        },
        '& .spectable-loading': {
            width: 2580,
        },
        '& .header': {
            position: 'absolute',
        },
        '& .body': {
            maxHeight: 800,
            height: 710,
        },
        '& .sticky .header': {
            zIndex: 5,
        },
    },
    projectDetailPopout: {
        padding: theme.spacing(1, 2),
        marginRight: theme.spacing(0.5),
    },
    projectName: {
        color: theme.palette.secondary.main,
        cursor: 'pointer',
    },
    tableViewCont: {
        paddingTop: 0,
    },
    isFavoriteColumnDiv: {
        display: 'flex',
        justifyContent: 'flex-start',
    },
    starIconClass: {
        padding: 'unset',
        color: theme.palette.grey[700],
    },
    actionIcon: {
        color: theme.palette.secondary.main,
        padding: theme.spacing(0.5),
        margin: 'auto',
    },
    projectGroupActionIcon: {
        color: theme.palette.secondary.main,
        padding: theme.spacing(0),
        margin: 'auto',
    },
    linkItem: {
        textDecoration: 'none',
    },
}))

const initialSorting = [
    { id: 'groupName', desc: false },
    { id: 'groupLastUpdated', desc: true },
    { id: 'lastUpdated', desc: true },
]
const groupBy = ['groupName']
interface ProjectTableViewProps {
    specTableTitleSettings: SpecTableTitleSettingsProps<TableProjectItem>
    projectData: Partial<ProjectItem>[] | undefined
    handleFavoriteChange?: (favorite: boolean, projectId: string) => void
    fetchNextData?: (
        props: Partial<TableFetchNextDataProps<TableProjectItem>>
    ) => void
    projectDataStatus?: number
    defaultPageSize?: number
    buildingTypeDropDown?: string[]
    projectPhaseDropDown?: string[]
    lodDropDown?: string[]
    setSkipCount?: (val: number) => void
    localStorageKey: string
    localStorageItemKey: (val: string) => string
    projectCount: number
    budgetDropDown?: string[]
    regionDropDown?: string[]
    countryDropDown?: string[]
    setProjectSortValues?: (val: Partial<GetSortInput>[]) => void
    projectSortValues?: Partial<GetSortInput>[]
    expanded: Record<SpecIdType<TableProjectItem>, boolean>
    setExpanded: (val: Record<SpecIdType<TableProjectItem>, boolean>) => void
    projectGroupCounts?: Partial<ProjectGroupCountItem>[]
    lazyLoading?: boolean
    projectsLoading: boolean
    triggerProjectListReload: VoidFunction
    isProjectHome?: boolean
    triggerRecentProjectsReload: React.Dispatch<React.SetStateAction<boolean>>
    openRemoveProjectConfirmation: (projectId: string | undefined) => void
}

const ProjectTableView: React.FC<ProjectTableViewProps> = ({
    specTableTitleSettings,
    projectData,
    handleFavoriteChange,
    fetchNextData,
    projectDataStatus,
    defaultPageSize,
    buildingTypeDropDown,
    projectPhaseDropDown,
    lodDropDown,
    setSkipCount,
    localStorageKey,
    localStorageItemKey,
    projectCount,
    budgetDropDown,
    regionDropDown,
    countryDropDown,
    setProjectSortValues,
    projectSortValues,
    expanded,
    projectsLoading,
    projectGroupCounts,
    setExpanded,
    triggerProjectListReload,
    isProjectHome,
    triggerRecentProjectsReload,
    openRemoveProjectConfirmation,
}) => {
    const classes = useStyles()
    const { data: currentUserInfoResponse } = useQuery<CurrentUserInfoResponse>(
        CurrentUserInfoQuery
    )
    const userId = currentUserInfoResponse?.currentUserInfo.kpUserId
    const { projectFilterValues, setProjectFilterValues } = React.useContext(
        ProjectFilterContext
    )
    const [inputFilterValues, setInputFilterValues] = React.useState<
        Partial<GetFilterInput>[]
    >(projectFilterValues)
    const [sortBy, setSortBy] = React.useState<
        SpecSortingRule<TableProjectItem>[]
    >(initialSorting)
    const [filters, setFilters] = useState<SpecFilters<TableProjectItem>>([])
    const [
        actionMenuAnchor,
        setActionMenuAnchor,
    ] = React.useState<null | Element>(null)

    const [initialExpandState, setInitialExpandState] = useState<boolean>(true)

    const [tableState, setTableState] = useState<
        SpecTableState<TableProjectItem>
    >()
    const [currentProject, setCurrentProject] = useState<
        Partial<TableProjectItem> | undefined
    >(undefined)

    const [
        projectPopoutAnchor,
        setProjectPopoutAnchor,
    ] = React.useState<null | Element>(null)
    const tableRef = useRef<HTMLDivElement>(null)
    const divContainer = useRef<HTMLDivElement>()

    const [
        projectGroupMenuAnchor,
        setProjectGroupMenuAnchor,
    ] = React.useState<null | Element>(null)

    const [projectGroupItem, setProjectGroupItem] = React.useState<string>('')

    const [
        isProjectGroupDialogOpen,
        setIsProjectGroupDialogOpen,
    ] = React.useState<boolean>(false)

    const [
        isRemoveProjectGroupDialogOpen,
        setIsRemoveProjectGroupDialogOpen,
    ] = React.useState<boolean>(false)

    const [uniqueProjectGroups, setUniqueProjectGroups] = React.useState<
        string[] | undefined
    >()

    const { isAdmin } = useReactiveVar(userInfo)

    const handleActionButtonClick = (proItem: Partial<TableProjectItem>) => (
        event: React.MouseEvent
    ) => {
        setActionMenuAnchor(event.currentTarget)
        setCurrentProject(proItem)
    }
    const handleActionMenuClose = () => {
        setActionMenuAnchor(null)
        setCurrentProject(undefined)
    }

    const handleProjectGroupButtonClick = (
        proGroupItem: Row<TableProjectItem>
    ) => (event: React.MouseEvent) => {
        setProjectGroupItem(proGroupItem.values.groupName)
        setProjectGroupMenuAnchor(event.currentTarget)
    }
    const handleProjectGroupButtonClose = () => {
        setProjectGroupMenuAnchor(null)
    }

    const toggleAddProjectGroupDialog = () => {
        if (isProjectGroupDialogOpen) {
            setProjectGroupItem('')
            setProjectGroupMenuAnchor(null)
        }
        setIsProjectGroupDialogOpen(!isProjectGroupDialogOpen)
    }

    const toggleRemoveProjectGroupDialog = () => {
        if (isRemoveProjectGroupDialogOpen) {
            setProjectGroupItem('')
            setProjectGroupMenuAnchor(null)
        }
        setIsRemoveProjectGroupDialogOpen(!isRemoveProjectGroupDialogOpen)
    }

    const handleFavoriteButtonClick = React.useCallback(
        (data: Row<TableProjectItem>) => {
            handleFavoriteChange?.(
                !data.original.isFavorite,
                data?.original?.id ?? ''
            )
        },
        [handleFavoriteChange]
    )
    const handleViewDetailsClick = () => {
        setProjectPopoutAnchor(actionMenuAnchor)
        setActionMenuAnchor?.(null)
    }
    const openRemoveProjectConfirmationAndCloseMenu = (
        projectId: string | undefined
    ) => {
        setActionMenuAnchor(null)
        openRemoveProjectConfirmation(projectId)
    }
    const handlePopoutClose = () => {
        setProjectPopoutAnchor(null)
    }

    React.useEffect(() => {
        if (projectData) {
            const uniqueProjectGroups = projectData
                ?.map(
                    (item) =>
                        `${item.groupName} (${
                            projectGroupCounts?.filter(
                                (itemCounts) =>
                                    item.groupId === itemCounts.groupId
                            )[0]?.totalCount ?? 0
                        })`
                )
                .filter((value, index, self) => self.indexOf(value) === index)
            setUniqueProjectGroups(uniqueProjectGroups)
        }
    }, [projectData, projectGroupCounts])

    React.useEffect(() => {
        if (initialExpandState && projectData) {
            const initialExpandedVal: Record<
                SpecIdType<TableProjectItem>,
                boolean
            > = {}
            if (tableRef.current) tableRef.current.scrollTop = 0

            const uniqueProjectGroups = projectData
                ?.map((item) => item.groupName)
                .filter((value, index, self) => self.indexOf(value) === index)
            uniqueProjectGroups.forEach((projectGroup, index) => {
                initialExpandedVal[`groupName:${projectGroup}`] =
                    index === 0 &&
                    !initialExpandedVal[`groupName:${projectGroup}`]
                        ? true
                        : false
            })
            setExpanded(initialExpandedVal)
            setInitialExpandState(false)
            const newTableState = tableState ? { ...tableState } : undefined
            if (newTableState?.expanded) {
                newTableState.expanded = initialExpandedVal
            }
            if (!isEqual(newTableState, tableState) || !tableState) {
                setTableState(newTableState)
            }
        }
    }, [projectData, initialExpandState, setExpanded, tableState])

    React.useEffect(() => {
        if (!!tableState && !isEqual(tableState.expanded, expanded)) {
            setExpanded(tableState.expanded)
        }
    }, [tableState, setExpanded, expanded])

    const actionColumnProps: ActionColumnProps<TableProjectItem> = {
        Cell: ({ cell }) => {
            if (cell.isAggregated) {
                if (isAdmin) {
                    return (
                        <IconButton
                            key={cell.row.index}
                            onClick={handleProjectGroupButtonClick(cell.row)}
                            className={clsx(
                                classes.projectGroupActionIcon,
                                'projectGroupIconButton'
                            )}
                            aria-label="project group button"
                        >
                            <MoreVertIcon fontSize="small" />
                        </IconButton>
                    )
                } else {
                    return <></>
                }
            } else {
                return (
                    <IconButton
                        data-testid="actionButton"
                        key={cell.row.index}
                        onClick={handleActionButtonClick(cell.row.original)}
                        className={classes.actionIcon}
                        aria-label="action button"
                    >
                        <MoreVertIcon fontSize="small" />
                    </IconButton>
                )
            }
        },
        width: 100,
    }

    const columns = useMemo<SpecColumn<TableProjectItem>[]>(
        () => [
            {
                Header: 'PROJECT GROUP',
                accessor: 'groupName',
                Cell: ({ cell }: CellProps<TableProjectItem, string>) => {
                    if (cell.isAggregated) return null
                    if (!cell.value) return 'Not Set'
                    const text = cell.value
                    return <span>{text}</span>
                },
                minWidth: 20,
                width: 80,
            },
            {
                Header: 'FAVORITE',
                accessor: 'isFavorite',
                disableFilters: true,
                maxWidth: 150,
                width: 102,
                // The cell can use the individual row's getToggleRowSelectedProps method
                // to the render a favorite icon
                Cell: ({ row }: Cell<TableProjectItem>) => {
                    const toggleRowSelectedProps = row.getToggleRowSelectedProps()
                    delete toggleRowSelectedProps.indeterminate
                    return !row.canExpand ? (
                        <div className={classes.isFavoriteColumnDiv}>
                            <ToggleIconButton
                                data-testid="toggleIconButton"
                                value={row.original?.isFavorite ?? false}
                                onClick={() => handleFavoriteButtonClick(row)}
                                onIcon={
                                    // @ts-expect-error

                                    <StarIcon
                                        {...{
                                            ...toggleRowSelectedProps,
                                            className: classes.starIconClass,
                                        }}
                                    />
                                }
                                offIcon={
                                    // @ts-expect-error

                                    <StarBorderIcon
                                        {...{
                                            ...toggleRowSelectedProps,
                                            className: classes.starIconClass,
                                        }}
                                    />
                                }
                            />
                        </div>
                    ) : null
                },
            },
            {
                Header: 'PROJECT NAME',
                accessor: 'name',
                minWidth: 200,
                width: 200,
                Cell: ({ cell }: CellProps<TableProjectItem, string>) => {
                    if (cell.isAggregated) return null
                    if (!cell.value) return 'Not Set'
                    const dashboardPath = `/projects/${cell.row.original.id}/dashboard`
                    return (
                        <HoverTooltip text={cell.value}>
                            <Link
                                to={dashboardPath}
                                className={classes.linkItem}
                            >
                                <span className={classes.projectName}>
                                    {cell.value}
                                </span>
                            </Link>
                        </HoverTooltip>
                    )
                },
            },
            {
                Header: 'PROJECT#',
                accessor: 'number',
                minWidth: 150,
                width: 170,
            },
            {
                Header: 'PHASE',
                accessor: 'phase',
                Cell: ({ cell }: CellProps<TableProjectItem, string>) => {
                    if (cell.isAggregated) return null
                    if (!cell.value) return 'Not Set'
                    return cell.value
                },
                minWidth: 150,
                width: 170,
                Filter: FilterSelectText({
                    options: projectPhaseDropDown ?? [],
                }),
            },
            {
                Header: 'LOS',
                accessor: 'lod',
                Cell: ({ cell }: CellProps<TableProjectItem, string>) => {
                    if (cell.isAggregated) return null
                    if (!cell.value) return 'Not Set'
                    return cell.value
                },
                minWidth: 150,
                width: 170,
                Filter: FilterSelectText({
                    options: lodDropDown ?? [],
                }),
            },
            {
                Header: 'Owner',
                accessor: 'owner',
                Cell: ({ cell }: CellProps<TableProjectItem, string>) => {
                    if (cell.isAggregated) return null
                    if (!cell.value) return 'Not Set'
                    return cell.value
                },
                minWidth: 120,
                width: 150,
            },
            {
                Header: 'PROJECT MANAGER',
                accessor: 'projectLeads',
                Cell: ({ cell }: CellProps<TableProjectItem, string[]>) => {
                    if (!cell.value) return null
                    if (!cell.value.length) return 'Not Set'
                    return cell.value.join(', ')
                },
                minWidth: 150,
                width: 170,
            },
            {
                Header: 'BUILDING TYPE',
                accessor: 'buildingType',
                Cell: ({ cell }: CellProps<TableProjectItem, string>) => {
                    if (cell.isAggregated) return null
                    if (!cell.value) return 'Not Set'
                    return cell.value
                },
                minWidth: 150,
                width: 170,
                Filter: FilterSelectText({
                    options: buildingTypeDropDown ?? [],
                }),
            },
            {
                Header: 'Gross Building Area',
                accessor: 'grossBuildingArea',
                Cell: ({ cell }: CellProps<TableProjectItem, string>) => {
                    if (cell.isAggregated) return null
                    if (!cell.value) return 'Not Set'
                    return cell.value
                },
                minWidth: 150,
                width: 170,
            },
            {
                Header: 'Construction Budget',
                accessor: 'budget',
                Cell: ({ cell }: CellProps<TableProjectItem, string>) => {
                    if (cell.isAggregated) return null
                    if (!cell.value) return 'Not Set'
                    return cell.value
                },
                minWidth: 150,
                width: 170,
                Filter: FilterSelectText({
                    options: budgetDropDown ?? [],
                }),
            },
            {
                Header: 'STATE/PROVINCE',
                accessor: 'region',
                Cell: ({ cell }: CellProps<TableProjectItem, string>) => {
                    if (cell.isAggregated) return null
                    if (!cell.value) return 'Not Set'
                    return cell.value
                },
                minWidth: 120,
                width: 150,
                Filter: FilterSelectText({
                    options: regionDropDown ?? [],
                }),
            },
            {
                Header: 'COUNTRY',
                accessor: 'country',
                Cell: ({ cell }: CellProps<TableProjectItem, string>) => {
                    if (cell.isAggregated) return null
                    if (!cell.value) return 'Not Set'
                    return cell.value
                },
                minWidth: 150,
                width: 170,
                Filter: FilterSelectText({
                    options: countryDropDown ?? [],
                }),
            },
            {
                Header: 'LAST UPDATED',
                accessor: 'lastUpdated',
                Cell: ({ cell }: CellProps<TableProjectItem, string>) => {
                    if (cell.isAggregated) return null
                    if (!cell.value) return 'Not Set'
                    return format(parseISO(cell.value), 'MM/dd/yyyy')
                },
                minWidth: 150,
                width: 170,
            },
        ],
        [
            budgetDropDown,
            buildingTypeDropDown,
            classes.isFavoriteColumnDiv,
            classes.projectName,
            classes.starIconClass,
            countryDropDown,
            handleFavoriteButtonClick,
            lodDropDown,
            projectPhaseDropDown,
            regionDropDown,
        ]
    )

    const [tableColumns, setTableColumns] = useState<
        SpecColumn<TableProjectItem>[]
    >(columns)

    React.useEffect(() => {
        const tempColumn = [...tableColumns]
        const setFilter = (accessor: string, options: string[]) => {
            const col = tempColumn.find((c) => c.accessor === accessor)
            if (col) col.Filter = FilterSelectText({ options })
        }
        if (buildingTypeDropDown) setFilter('buildingType', buildingTypeDropDown)
        if (projectPhaseDropDown) setFilter('phase', projectPhaseDropDown)
        if (lodDropDown) setFilter('lod', lodDropDown)
        if (budgetDropDown) setFilter('budget', budgetDropDown)
        if (regionDropDown) setFilter('region', regionDropDown)
        if (countryDropDown) setFilter('country', countryDropDown)
        if (!isEqual(tempColumn, tableColumns)) {
            setTableColumns(tempColumn)
        }
    }, [
        projectPhaseDropDown,
        lodDropDown,
        budgetDropDown,
        buildingTypeDropDown,
        regionDropDown,
        countryDropDown,
        tableColumns,
    ])

    const fetchData = React.useCallback(
        ({ filters: updatedFilters, sortBy: updatedSortBy }) => {
            if (!!updatedFilters) {
                setFilters(updatedFilters)
            }
            if (!!updatedSortBy && updatedSortBy.length) {
                setSortBy(updatedSortBy)
            }
        },
        [setSortBy, setFilters]
    )

    const updateFilterCacheData = (inputValue: Partial<GetFilterInput>[]) => {
        if (userId) {
            const saved = getLocalStorageValue(
                localStorageItemKey(userId),
                userId,
                '',
                localStorageKey
            )
            const pastItems = saved !== '' ? JSON.parse(saved as string) : {}
            updateKpMainLocalStore({
                currentUserId: userId,
                pageKey: localStorageKey,
                itemKey: localStorageItemKey(userId),
                itemValue: JSON.stringify({
                    ...pastItems,
                    filterValues: inputValue,
                }),
            })
        }
    }

    const debouncedRaiseOnInput = useCallback(
        debounce((inputValue: Partial<GetFilterInput>[]) => {
            if (setSkipCount) {
                setSkipCount(0)
            }
            updateFilterCacheData(inputValue)
            setProjectFilterValues(inputValue)
        }),
        [setSkipCount, updateFilterCacheData, setProjectFilterValues]
    )

    React.useEffect(() => {
        const projectFilterValuesList: Partial<GetFilterInput>[] = []
        if (filters) {
            filters.forEach((item) => {
                if (item.value?.length) {
                    const obj: Partial<GetFilterInput> = {
                        itemField:
                            item.id === 'buildingType'
                                ? 'Building Type'
                                : item.id,
                        filterValues: Array.isArray(item.value)
                            ? item.value
                            : [item.value],
                    }
                    projectFilterValuesList.push(obj)
                }
            })
            setInputFilterValues(projectFilterValuesList)
            debouncedRaiseOnInput(projectFilterValuesList)
        }
    }, [filters])

    const updateSortCacheData = useCallback(
        (inputValue: Partial<GetSortInput>[]) => {
            if (userId) {
                const saved = getLocalStorageValue(
                    localStorageItemKey(userId),
                    userId,
                    '',
                    localStorageKey
                )
                const pastItems =
                    saved !== '' ? JSON.parse(saved as string) : {}
                updateKpMainLocalStore({
                    currentUserId: userId,
                    pageKey: localStorageKey,
                    itemKey: localStorageItemKey(userId),
                    itemValue: JSON.stringify({
                        ...pastItems,
                        sortValues: inputValue,
                    }),
                })
            }
        },
        [localStorageItemKey, localStorageKey, userId]
    )

    React.useEffect(() => {
        const projectSortValueList: Partial<GetSortInput>[] = []
        const newSortList = sortBy && sortBy.length ? sortBy : initialSorting
        if (newSortList && newSortList.length) {
            setSkipCount?.(0)
            sortBy.forEach((item) => {
                const obj: Partial<GetSortInput> = {
                    sortField:
                        item.id === 'buildingType' ? 'Building Type' : item.id,
                    sortOrder: item.desc ? 'desc' : 'asc',
                }
                projectSortValueList.push(obj)
            })
            updateSortCacheData(projectSortValueList)
            setProjectSortValues?.(projectSortValueList)
        }
    }, [sortBy, setProjectSortValues, setSkipCount, updateSortCacheData])

    /**
     * useSpecTableOptions give you insight of all the exposed props that you can
     * do within the SpecTable.
     */
    const tableProps = React.useMemo(() => {
        return {
            actionColumnProps,
            allowGrouping: true,
            columns: tableColumns,
            data: (getStructuredTableView(projectData, projectGroupCounts) ??
                []) as TableProjectItem[],
            divContainerRef: divContainer,
            expanded,
            fetchData,
            fetchDataLoading:
                projectDataStatus !== NetworkStatus.ready &&
                projectDataStatus !== NetworkStatus.fetchMore,
            fetchNextData,
            fetchNextDataLoading: projectDataStatus !== NetworkStatus.ready,
            filters: tableFilterInput(inputFilterValues),
            groupBy,
            height: 'auto',
            onStateChange: setTableState,
            pageSize: defaultPageSize,
            showCheckboxColumn: false,
            specTableTitleSettings,
            totalItems: projectCount,
            sortBy: tableSortInput(projectSortValues),
            isProjectHome,
        }
    }, [
        defaultPageSize,
        inputFilterValues,
        projectData,
        projectDataStatus,
        tableColumns,
        actionColumnProps,
        projectCount,
        expanded,
        fetchData,
        fetchNextData,
        setTableState,
        projectSortValues,
        specTableTitleSettings,
    ])
    const specTableProps = useSpecTableOptions<TableProjectItem>(tableProps)
    return (
        <>
            <GridContainer
                className={classes.tableViewCont}
                data-testid={'projectTableContainer'}
            >
                <GridItem
                    xs={12}
                    ref={divContainer}
                    className={classes.specTableStyle}
                >
                    <SpecTable
                        {...specTableProps}
                        lazyLoading={projectData?.length !== projectCount}
                        height={700}
                        projectsLoading={projectsLoading}
                    />
                </GridItem>
            </GridContainer>
            <ActionColumnPopup
                isAdmin={isAdmin}
                actionMenuAnchor={actionMenuAnchor}
                handleActionMenuClose={handleActionMenuClose}
                currentProject={currentProject}
                handleViewDetailsClick={handleViewDetailsClick}
                openRemoveProjectConfirmation={
                    openRemoveProjectConfirmationAndCloseMenu
                }
            />
            <ProjectGroupActionPopup
                projectGroupList={uniqueProjectGroups}
                projectGroupMenuAnchor={projectGroupMenuAnchor}
                handleProjectGroupMenuClose={handleProjectGroupButtonClose}
                handleOpenEditProjectDialog={toggleAddProjectGroupDialog}
                handleOpenRemoveProjectDialog={toggleRemoveProjectGroupDialog}
            />
            <Popout
                offset="100"
                placement="right"
                className={clsx(
                    'projectTablePopoutInfo',
                    classes.projectDetailPopout
                )}
                anchorEl={projectPopoutAnchor}
                onCloseRequest={handlePopoutClose}
                theme="dark"
            >
                <PopoutInfoContent projectItem={currentProject} />
            </Popout>
            {isProjectGroupDialogOpen && (
                <ProjectGroupDialog
                    openDialog={isProjectGroupDialogOpen}
                    handleDialogClose={toggleAddProjectGroupDialog}
                    refetch={triggerProjectListReload}
                    groupName={projectGroupItem}
                />
            )}
            {isRemoveProjectGroupDialogOpen && (
                <RemoveProjectGroupDialog
                    projectGroupCount={projectGroupCounts}
                    openDialog={isRemoveProjectGroupDialogOpen}
                    handleDialogClose={toggleRemoveProjectGroupDialog}
                    refetch={triggerProjectListReload}
                    groupName={projectGroupItem}
                    refetchRecent={triggerRecentProjectsReload}
                />
            )}
        </>
    )
}

const getStructuredTableView = (
    projectData: Partial<ProjectItem>[] | undefined,
    projectGroupCountItems?: Partial<ProjectGroupCountItem>[]
) => {
    const finalData: Partial<TableProjectItem>[] = []
    if (projectData && projectGroupCountItems) {
        projectData.forEach((proItem: Partial<ProjectItem>) => {
            const currentMilestone = proItem?.currentMilestone
            finalData.push({
                groupName: proItem.groupName,
                isFavorite: proItem.isFavorite,
                name: proItem.name,
                number: proItem.number,
                id: proItem.id,
                buildingType:
                    getAssociationModifier({
                        parameters: proItem?.parameters,
                        modifierListName: 'Building Type',
                    })?.join(', ') ?? 'Not Set',
                phase: currentMilestone?.phase?.name ?? 'Not Set',
                lod: !currentMilestone?.phase?.name
                    ? undefined
                    : currentMilestone.phase.loD,
                region: proItem.location?.region,
                country: proItem.location?.country,
                lastUpdated: proItem.lastUpdated,
                projectLeads: proItem.projectLeads,
                budget: proItem.budget,
                location: proItem.location,
                parameters: proItem.parameters,
                owner:
                    getAssociationModifier({
                        parameters: proItem?.parameters,
                        modifierListName: 'Owner Type',
                    })?.join(', ') ?? 'Not Set',
                grossBuildingArea: proItem.grossBuildingArea,
                currentMilestone: currentMilestone,
                groupCount: projectGroupCountItems
                    ? projectGroupCountItems.filter(
                          (item) => item.groupId === proItem.groupId
                      )[0]?.totalCount ?? 0
                    : 0,
            })
        })
    }
    return finalData
}

export const tableFilterInput = (filter: Partial<GetFilterInput>[]) => {
    const filterValue: SpecFilters<TableProjectItem> = []
    if (filter && filter.length) {
        filter.forEach((item: Partial<GetFilterInput>) => {
            if (item && item.filterValues?.length) {
                if (item.itemField === 'Building Type') {
                    filterValue.push({
                        id: 'buildingType',
                        value: item.filterValues,
                    })
                } else if (
                    item.itemField === 'phase' ||
                    item.itemField === 'lod' ||
                    item.itemField === 'country' ||
                    item.itemField === 'region' ||
                    item.itemField === 'budget'
                ) {
                    filterValue.push({
                        id: item.itemField ?? '',
                        value: item.filterValues,
                    })
                } else {
                    filterValue.push({
                        id: item?.itemField ?? '',
                        value: item?.filterValues?.[0] ?? [],
                    })
                }
            }
        })
    }
    return filterValue
}

export const tableSortInput = (sort: Partial<GetSortInput>[] | undefined) => {
    let sortValue: SpecSortingRule<TableProjectItem>[] = []
    if (sort && sort.length) {
        sort.forEach((item: Partial<GetSortInput>) => {
            if (item.sortField === 'Building Type') {
                sortValue.push({
                    id: 'buildingType',
                    desc: item.sortOrder === 'desc' ? true : false,
                })
            } else {
                sortValue.push({
                    id: item?.sortField ?? '',
                    desc: item.sortOrder === 'desc' ? true : false,
                })
            }
        })
    } else sortValue = [...initialSorting]
    return sortValue
}

export default ProjectTableView
